import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Landmark, RefreshCw, Search } from 'lucide-react';
import { apiErrorMessage, getAccountsBoard } from '../api/abfApi';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../components/ActionFeedback';
import { Header, SmallButton } from '../onboarding/review/contractReviewStyles';
import { INK } from '../onboarding/review/reviewStyles';
import { ABF_HOME_PATH, facilityWorkspacePath } from '../routes';
import type { AccountsBoard, AccountsBoardRow } from '../types/facilityWorkspace';
import { Shell, money, words } from './boardShared';

/**
 * WS4 — every contract-defined bank account across facilities (Oaktree row 7.2).
 *
 * Answers first: how much cash the statements show, which accounts need attention, where cash
 * is unidentified. Then one card per facility, its holder funds and its accounts, each linking
 * to the facility's Collections account map where the work is done. Balances are as of each
 * account's last statement; the page says so once.
 */

const Kpis = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 0 0 16px;
  > div { padding: 14px 16px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised}; }
  span { display: block; font-size: 12px; color: ${INK.muted}; letter-spacing: .02em; }
  strong { display: block; margin-top: 4px; font-size: 22px; font-weight: 700; color: ${INK.strong}; font-variant-numeric: tabular-nums; }
  small { display: block; margin-top: 3px; font-size: 12px; color: ${INK.muted}; }
  .warn strong { color: #edcc82; } .bad strong { color: #f8a5a5; }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;
const Bar = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 0 0 14px;
  .search { display: flex; align-items: center; gap: 8px; flex: 1 1 280px; padding: 0 12px; min-height: 38px; border: 1px solid ${INK.lineStrong}; border-radius: 9px; background: ${INK.panel}; color: ${INK.muted}; }
  .search input { flex: 1; border: 0; background: transparent; color: ${INK.body}; font: inherit; font-size: 14px; outline: none; }
  .seg { display: inline-flex; border: 1px solid ${INK.lineStrong}; border-radius: 9px; overflow: hidden; }
  .seg button { min-height: 36px; padding: 0 13px; border: 0; background: transparent; color: ${INK.soft}; font: inherit; font-size: 13px; cursor: pointer; }
  .seg button + button { border-left: 1px solid ${INK.lineStrong}; }
  .seg button[aria-pressed='true'] { background: #12273c; color: ${INK.strong}; box-shadow: inset 0 -2px #67c6f4; }
`;
const FacilityCard = styled.section`
  margin: 0 0 12px; border: 1px solid ${INK.line}; border-radius: 11px; background: ${INK.raised}; overflow: hidden;
  > header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 13px 16px; cursor: pointer; }
  > header:hover { background: #10203a; }
  .title { font-size: 15px; font-weight: 700; color: ${INK.strong}; }
  .title small { font-weight: 400; color: ${INK.muted}; margin-left: 8px; font-size: 13px; }
  .sub { margin-top: 3px; font-size: 12px; color: ${INK.muted}; }
  .right { display: flex; align-items: center; gap: 14px; }
  .cash { text-align: right; font-variant-numeric: tabular-nums; } .cash strong { display: block; font-size: 16px; color: ${INK.strong}; } .cash small { font-size: 11px; color: ${INK.muted}; }
  .open { display: inline-flex; align-items: center; gap: 5px; color: ${INK.accentSoft}; font-size: 13px; text-decoration: none; white-space: nowrap; }
`;
const Rows = styled.div`
  border-top: 1px solid ${INK.line};
  .row { display: grid; grid-template-columns: minmax(0, 2.1fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.7fr) minmax(0, .8fr); gap: 14px; padding: 11px 16px; align-items: start; }
  .row + .row { border-top: 1px solid ${INK.line}; }
  .head { padding-top: 8px; padding-bottom: 8px; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: ${INK.muted}; background: ${INK.panel}; }
  .name { font-weight: 600; color: ${INK.strong}; font-size: 14px; }
  .meta { font-size: 12px; color: ${INK.muted}; margin-top: 2px; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .num strong { font-size: 15px; color: ${INK.strong}; }
  .note { display: block; margin-top: 4px; font-size: 12px; color: ${INK.muted}; }
  @media (max-width: 1000px) { .row { grid-template-columns: 1fr 1fr; } .head { display: none; } .num { text-align: left; } }
`;

type Status = 'ALL' | 'ATTENTION' | 'CURRENT';
const statusTone = (s: string): StatusTone => s === 'CURRENT' ? 'done' : s === 'NO_STATEMENTS' ? 'neutral' : 'warning';
const statusLabel = (s: string) => s === 'CURRENT' ? 'current' : s === 'STALE' ? 'stale' : s === 'NO_BALANCES' ? 'no balance' : 'no statement';
const needsAttention = (r: AccountsBoardRow) => r.feedStatus !== 'CURRENT' || Number(r.unidentifiedCredits || 0) > 0 || Number(r.reserveShortfall || 0) > 0;

const AccountsBoardPage: React.FC = () => {
  const [board, setBoard] = useState<AccountsBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState<Status>('ALL');
  const [open, setOpen] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setBoard(await getAccountsBoard()); }
    catch (error) { setFeedback({ tone: 'error', message: apiErrorMessage(error, 'The bank accounts could not be loaded.') }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => board?.accounts ?? [], [board]);
  const types = useMemo(() => Array.from(new Set(rows.map(r => r.accountType))).sort(), [rows]);
  const q = query.trim().toLowerCase();
  const visible = rows.filter(r => (!type || r.accountType === type)
    && (status === 'ALL' || (status === 'ATTENTION' ? needsAttention(r) : r.feedStatus === 'CURRENT'))
    && (!q || [r.facilityCode, r.facilityName, r.accountName, r.accountKey, r.bankName, r.maskedAccountNumber, ...(r.holderFunds || []).map(h => h.portfolio_code)]
      .some(v => String(v || '').toLowerCase().includes(q))));
  const groups = useMemo(() => {
    const by = new Map<number, AccountsBoardRow[]>();
    for (const r of visible) by.set(r.facilityId, [...(by.get(r.facilityId) || []), r]);
    return Array.from(by.values()).sort((a, b) => Number(b.some(needsAttention)) - Number(a.some(needsAttention)) || a[0].facilityCode.localeCompare(b[0].facilityCode));
  }, [visible]);

  const t = board?.totals;
  const reporting = rows.filter(r => r.feedStatus === 'CURRENT').length;
  const attention = rows.filter(needsAttention).length;
  const unidentified = rows.reduce((sum, r) => sum + Number(r.unidentifiedCredits || 0), 0);
  // A facility none of whose accounts has ever sent a statement starts folded: it is one fact, not N rows.
  const isOpen = (g: AccountsBoardRow[]) => open[g[0].facilityId] ?? !g.every(r => r.feedStatus === 'NO_STATEMENTS');

  return <Shell>
    <Header>
      <Link className="back" to={ABF_HOME_PATH}><ArrowLeft size={14} /> ABF home</Link>
      <div className="heading">
        <div>
          <div className="eyebrow">Asset-based finance</div>
          <h1><Landmark size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Bank accounts</h1>
          <div className="meta"><span>Business date {board?.businessDate ?? '…'}</span>{t && <span>{t.accounts} accounts on {t.facilities} facilities</span>}<span>Balances as of each account's last statement; no live bank feed is implied</span></div>
        </div>
        <div className="actions"><SmallButton type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}</SmallButton></div>
      </div>
    </Header>

    {t && <Kpis aria-label="Account totals">
      <div><span>Cash on statements</span><strong>{Object.entries(t.closingByCurrency).map(([ccy, v]) => money(v, ccy)).join(' · ') || '—'}</strong><small>sum of the last statement balances</small></div>
      <div><span>Reporting</span><strong>{reporting} of {t.accounts}</strong><small>accounts with a current balance</small></div>
      <div className={attention ? 'warn' : undefined}><span>Need attention</span><strong>{attention}</strong><small>{t.noStatements} no statement · {t.staleOrNoBalance} stale or no balance</small></div>
      <div className={unidentified ? 'bad' : undefined}><span>Unidentified credits</span><strong>{unidentified ? money(unidentified) : '—'}</strong><small>{t.facilitiesWithUnidentified} facilit{t.facilitiesWithUnidentified === 1 ? 'y' : 'ies'}</small></div>
    </Kpis>}

    <Bar>
      <label className="search"><Search size={15} /><input aria-label="Search accounts" placeholder="Search facility, fund, bank or account" value={query} onChange={e => setQuery(e.target.value)} /></label>
      <div className="seg" role="group" aria-label="Account type">
        <button type="button" aria-pressed={!type} onClick={() => setType('')}>All types</button>
        {types.map(x => <button key={x} type="button" aria-pressed={type === x} onClick={() => setType(x)}>{words(x)}</button>)}
      </div>
      <div className="seg" role="group" aria-label="Status">
        {(['ALL', 'ATTENTION', 'CURRENT'] as Status[]).map(s => <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}>{s === 'ALL' ? 'All' : s === 'ATTENTION' ? 'Needs attention' : 'Current'}</button>)}
      </div>
    </Bar>

    {groups.map(g => {
      const f = g[0];
      const expanded = isOpen(g);
      const cash = g.reduce((sum, r) => sum + Number(r.closingBalance || 0), 0);
      const counts = g.reduce<Record<string, number>>((c, r) => ({ ...c, [r.feedStatus]: (c[r.feedStatus] || 0) + 1 }), {});
      const holders = (f.holderFunds || []).map(h => `${h.portfolio_code || h.portfolio_name} ${Number(h.share_pct).toFixed(Number(h.share_pct) % 1 ? 2 : 0)}%`).join(' · ') || f.holderName || 'no holder fund';
      return <FacilityCard key={f.facilityId}>
        <header onClick={() => setOpen(o => ({ ...o, [f.facilityId]: !expanded }))} aria-expanded={expanded}>
          {expanded ? <ChevronDown size={16} color={INK.muted} /> : <ChevronRight size={16} color={INK.muted} />}
          <div>
            <div className="title">{f.facilityCode}<small>{f.facilityName}</small></div>
            <div className="sub">Held by {holders} · period {f.periodLabel} · {Object.entries(counts).map(([s, n]) => `${n} ${statusLabel(s)}`).join(' · ')}</div>
          </div>
          <div className="right">
            <div className="cash"><strong>{cash ? money(cash, f.currency || undefined) : '—'}</strong><small>on statements</small></div>
            <Link className="open" to={facilityWorkspacePath(f.facilityId, 'collections') + `?periodId=${f.periodId}`} onClick={e => e.stopPropagation()}>Account map <ArrowRight size={14} /></Link>
          </div>
        </header>
        {expanded && <Rows>
          <div className="row head"><div>Account</div><div className="num">Balance</div><div className="num">This period in / out</div><div>Status</div><div className="num">Unidentified</div></div>
          {g.map(r => <div className="row" key={r.id}>
            <div>
              <div className="name">{r.accountName || r.accountKey} <StatusChip tone="neutral" label={words(r.accountType)} size="sm" /></div>
              <div className="meta">{r.bankName || 'bank not stated'}{r.maskedAccountNumber ? ` · ${r.maskedAccountNumber}` : ''}{r.sweepToAccountKey ? ` · sweeps to ${words(r.sweepToAccountKey)}${r.sweepFrequency ? ` ${r.sweepFrequency.toLowerCase()}` : ''}` : ''}</div>
              {r.controlAgreement && <div className="meta">Control: {r.controlAgreement}</div>}
            </div>
            <div className="num"><strong>{money(r.closingBalance, r.currency || undefined)}</strong>{r.balanceAsOf && <span className="note">as of {r.balanceAsOf}</span>}
              {r.reserveShortfall != null && Number(r.reserveShortfall) > 0 && <span className="note" style={{ color: '#f8a5a5' }}>reserve short {money(r.reserveShortfall, r.currency || undefined)}</span>}</div>
            <div className="num">{Number(r.credits) || Number(r.debits) ? <>{money(r.credits, r.currency || undefined)}<span className="note">out {money(r.debits, r.currency || undefined)}</span></> : <span style={{ color: INK.muted }}>no movement</span>}</div>
            <div><StatusChip tone={statusTone(r.feedStatus)} label={statusLabel(r.feedStatus)} size="sm" />{r.feedNote && <span className="note">{r.feedNote}</span>}</div>
            <div className="num">{Number(r.unidentifiedCredits || 0) > 0 ? <strong style={{ color: '#f8a5a5' }}>{money(r.unidentifiedCredits, r.currency || undefined)}</strong> : <span style={{ color: INK.muted }}>—</span>}</div>
          </div>)}
        </Rows>}
      </FacilityCard>;
    })}
    {!groups.length && !loading && <div style={{ padding: '1.4rem', textAlign: 'center', color: INK.muted, border: `1px dashed ${INK.lineStrong}`, borderRadius: 10 }}>{rows.length ? 'No account matches.' : 'No facility has a contract-defined account with a collection period yet.'}</div>}
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Shell>;
};

export default AccountsBoardPage;
